import { createFileRoute } from "@tanstack/react-router";

/**
 * Personvernerklaering.
 *
 * Laa foer paa en Convex-adresse, bold-pony-700.eu-west-1.convex.site, og
 * kunden ble sendt ut av nettsiden til et domene med et navn som ikke betyr
 * noe for dem. En personvernside skal vaere det man stoler mest paa, og da
 * kan den ikke ligge et sted som ser ut som en tilfeldig server.
 *
 * Teksten er den samme, skrevet i klarspraak fordi personvernforordningen
 * artikkel 12 krever det. En juridisk korrekt tekst ingen forstaar oppfyller
 * ikke informasjonsplikten.
 */

const NAVN = "Drammen Car Wash";
const EPOST = "drammen.car.wash@gmail.com";

const UNDERLEVERANDORER: [string, string, string][] = [
  ["Ascendly", "drifter booking, kundeoppfølging og utsendinger", "Norge"],
  ["Convex", "database og drift", "EU (Irland)"],
  ["Resend", "utsending av e-post", "USA"],
  ["Twilio", "utsending og mottak av SMS", "USA"],
  ["Meta", "meldinger på WhatsApp", "USA"],
  ["Google", "kalender, anmeldelser og annonser", "USA/EU"],
  ["Statens vegvesen", "oppslag på registreringsnummer", "Norge"],
];

export const Route = createFileRoute("/personvern")({
  head: () => ({
    meta: [
      { title: `Personvern | ${NAVN}` },
      {
        name: "description",
        content: `Slik behandler ${NAVN} personopplysningene dine når du bestiller time på nettsiden.`,
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PersonvernPage,
});

function Seksjon({ tittel, children }: { tittel: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-6 shadow-soft">
      <h2 className="font-serif text-lg">{tittel}</h2>
      <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

function PersonvernPage() {
  return (
    <main className="bg-background text-foreground">
      <div className="mx-auto w-full max-w-3xl px-4 py-16 sm:px-6 sm:py-24">
        <h1 className="font-serif text-3xl sm:text-4xl text-balance">Personvern hos {NAVN}</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Gjelder bestillinger og henvendelser gjort på nettsiden.
        </p>

        <div className="mt-10 space-y-3">
          <Seksjon tittel="Hvem som er ansvarlig">
            <p>
              <strong>{NAVN}</strong> bestemmer hva opplysningene dine brukes til, og er
              ansvarlig for dem. Kontakt: <a className="text-primary underline underline-offset-2" href={`mailto:${EPOST}`}>{EPOST}</a>.
            </p>
            <p>
              Ascendly (org.nr 937 370 865) drifter bookingsystemet på vegne av {NAVN}, og
              behandler opplysningene kun etter instruks fra dem. Det er regulert i en egen
              databehandleravtale.
            </p>
          </Seksjon>

          <Seksjon tittel="Hva vi lagrer om deg">
            <ul className="list-disc space-y-1 pl-5">
              <li>Navn, telefonnummer og e-postadresse</li>
              <li>Hvilken tjeneste du bestilte, når, og hva den kostet</li>
              <li>Registreringsnummer og bilmodell, der tjenesten avhenger av bilen</li>
              <li>Bekreftelser, påminnelser og andre meldinger vi har sendt deg</li>
            </ul>
            <p>Vi lagrer ikke helseopplysninger, fødselsnummer eller betalingskortnummer.</p>
          </Seksjon>

          <Seksjon tittel="Hvorfor, og med hvilken rett">
            <p>
              <strong>For å levere det du bestilte.</strong> Bekrefte timen, minne deg på den,
              og holde oversikt over hva som er gjort. Grunnlaget er avtalen mellom deg og{" "}
              {NAVN}, personvernforordningen artikkel 6 nr. 1 bokstav b.
            </p>
            <p>
              <strong>For å be om en anmeldelse eller sende deg tilbud.</strong> Grunnlaget er
              samtykke og markedsføringsloven § 15. Du kan si nei når som helst, se under.
            </p>
            <p>
              <strong>For drift og feilsøking.</strong> Berettiget interesse, artikkel 6 nr. 1
              bokstav f.
            </p>
          </Seksjon>

          <Seksjon tittel="Hvordan du sier nei til markedsføring">
            <p>
              Hver markedsførings-e-post har en avmeldingslenke nederst. Ett klikk, ingen
              innlogging. På SMS svarer du <strong>STOPP</strong>.
            </p>
            <p>
              Du får fortsatt bekreftelse og påminnelse for timer du selv har bestilt. Det er
              ikke markedsføring, og en avbestilt påminnelse hjelper ingen.
            </p>
          </Seksjon>

          <Seksjon tittel="Hvem opplysningene deles med">
            <p>
              De selges aldri. Disse leverandørene brukes for å få tjenesten til å virke, og
              hver av dem får bare det de trenger:
            </p>
            <div className="-mx-1 mt-3 overflow-x-auto">
              <table className="w-full min-w-[26rem] border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="border-b border-border px-2 py-2 text-left font-semibold text-foreground/70">Leverandør</th>
                    <th className="border-b border-border px-2 py-2 text-left font-semibold text-foreground/70">Hva de gjør</th>
                    <th className="border-b border-border px-2 py-2 text-left font-semibold text-foreground/70">Hvor</th>
                  </tr>
                </thead>
                <tbody>
                  {UNDERLEVERANDORER.map(([n, hva, sted]) => (
                    <tr key={n}>
                      <td className="border-b border-border px-2 py-2 align-top">{n}</td>
                      <td className="border-b border-border px-2 py-2 align-top">{hva}</td>
                      <td className="border-b border-border px-2 py-2 align-top">{sted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3">
              Noen av dem ligger utenfor EØS. Overføringen skjer da på EUs standard
              personvernbestemmelser (SCC).
            </p>
          </Seksjon>

          <Seksjon tittel="Hvor lenge det lagres">
            <p>
              Så lenge du er kunde hos {NAVN}, og så lenge {NAVN} bruker Ascendly.
              Opplysningene brukes til å se hva som er gjort på bilen din tidligere, og til å
              minne deg på neste gang.
            </p>
            <p>
              Vil du bli slettet før det, si fra til{" "}
              <a className="text-primary underline underline-offset-2" href={`mailto:${EPOST}`}>{EPOST}</a>, så gjør vi det.
            </p>
            <p>
              Har du meldt deg av markedsføring, lagrer vi selve avmeldingen på ubestemt tid.
              Det er nødvendig: skal vi kunne la være å kontakte deg, må vi huske at du har
              sagt nei.
            </p>
          </Seksjon>

          <Seksjon tittel="Rettighetene dine">
            <p>
              Du har rett til å se hva vi har om deg, få rettet feil, få det slettet, begrense
              eller protestere mot bruken, og få opplysningene utlevert i et maskinlesbart
              format.
            </p>
            <p>
              Send en e-post til <a className="text-primary underline underline-offset-2" href={`mailto:${EPOST}`}>{EPOST}</a>, så
              får du svar innen 30 dager.
            </p>
            <p>
              Mener du vi behandler opplysninger feil, kan du klage til Datatilsynet,{" "}
              <a className="text-primary underline underline-offset-2" href="https://www.datatilsynet.no" rel="noopener noreferrer" target="_blank">
                datatilsynet.no
              </a>
              .
            </p>
          </Seksjon>

          <Seksjon tittel="Sikkerhet">
            <p>
              Opplysningene lagres i EU og overføres kryptert. Tilgang krever innlogging, og en
              innlogging gir bare tilgang til {NAVN} sine egne data.
            </p>
          </Seksjon>
        </div>

        <p className="mt-10 text-center text-xs text-muted-foreground">
          Levert av{" "}
          <a className="text-primary underline underline-offset-2" href="https://ascendly.no/personvern" rel="noopener noreferrer" target="_blank">
            Ascendly
          </a>
        </p>
      </div>
    </main>
  );
}
